/* global axios */
// Softphone AstraCall — Central de Ligações ao vivo. Áudio = track WebRTC (Opus);
// VÍDEO = H264 sobre DataChannel via WebCodecs (setupVideoChannel), sempre aberto p/
// upgrade mid-call sem renegociar SDP. Browser-direct (CORS liberado, SSE por apiKey).
// Um RTCPeerConnection por call_id. Vídeo só em Chrome/Edge; senão áudio-only.
import { reactive, computed } from 'vue';
import { useCallsStore } from 'dashboard/stores/calls';
import { emitter } from 'shared/helpers/mitt';
import { setupVideoChannel } from './astracall/videoChannel';
import {
  videoSupported,
  VIDEO_WIDTH,
  VIDEO_HEIGHT,
  VIDEO_FPS,
} from './astracall/videoCodec';

const cameraConstraints = () => ({
  width: { ideal: VIDEO_WIDTH },
  height: { ideal: VIDEO_HEIGHT },
  frameRate: { ideal: VIDEO_FPS },
});

const waitIceComplete = pc =>
  new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const check = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', check);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', check);
  });

// estado singleton (compartilhado por todo o painel)
const state = reactive({
  ready: false,
  enabled: false,
  baseUrl: '',
  apiKey: '',
  clientId: '',
  sessions: [], // [{ sid, name, inbox_id, inbox_name }]
  calls: {}, // por callId: { callId, sid, status, direction, peer, held, startedAt, muted }
  // callIds com ponte de áudio WebRTC ATIVA neste navegador (reativo p/ os getters).
  // Só chamadas aqui contam como "ativas" no dock — evita hidratar fantasmas do
  // call-list (chamadas presas no call server sem call-ended) como se fossem desta aba.
  bridged: {},
});

const peers = {}; // callId -> { pc, audioEl, localStream, video, remoteVideoStream, localVideoStream, setLocalVideo }
let es = null;
let initPromise = null;

const CLIENT_KEY = 'astracall_client_id';
const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

const getClientId = () => {
  let id = localStorage.getItem(CLIENT_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(CLIENT_KEY, id);
  }
  return id;
};

// ---- REST direto no call server ----
const rest = async (sid, path, { method = 'POST', body } = {}) => {
  const res = await fetch(`${state.baseUrl}/api/sessions/${sid}/${path}`, {
    method,
    headers: {
      'X-API-Key': state.apiKey,
      'X-Client-Id': state.clientId,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
};

// ---- estado das chamadas ----
// O call server fala `inbound`/`outbound` no snapshot `call-list` e `incoming` no
// evento avulso — e o snapshot chega DEPOIS, sobrescrevendo o direction do evento.
// Sem normalizar aqui, a chamada de entrada virava `inbound`, `incomingCalls`
// ficava vazia e o CallDock nunca tocava.
const DIRECTION_ALIAS = {
  inbound: 'incoming',
  incoming: 'incoming',
  outbound: 'outgoing',
  outgoing: 'outgoing',
};
// `1449…@lid` (LID de privacidade), `…@s.whatsapp.net`, `…@g.us`: endereçamento
// interno do WhatsApp, não telefone. Não serve para mostrar nem para o histórico.
const ehEnderecoInterno = v => String(v || '').includes('@');

// Identidade resolvida pelo backend (nome/telefone do contato), por callId. Fica
// guardada à parte para sobreviver a qualquer recriação da entrada a partir de um
// retrato do servidor, que só carrega o endereço interno.
const identidadeResolvida = {};

// Sessões cuja caixa tem bot de voz ativo. A IA atende sozinha, então o card e o
// toque NÃO podem aparecer: o atendente entraria numa conversa que não é dele. O
// ticket é quem mostra que existe ligação em andamento com a IA.
const temBotDeVoz = sid =>
  !!sid && state.sessions.some(s => s.sid === sid && s.voice_bot);

// Chamadas que a IA nos entregou (transferência): essas PODEM tocar, mesmo vindo
// de uma sessão com bot — foi a própria IA que pediu para chamar este atendente.
const entreguesPelaIa = {};

const upsertCall = c => {
  const id = c.callId || c.id;
  if (!id) return;
  const anterior = state.calls[id] || {};
  const patch = { ...c, callId: id };
  const dir = DIRECTION_ALIAS[String(patch.direction || '').toLowerCase()];
  if (dir) patch.direction = dir;
  else delete patch.direction; // não deixa um valor desconhecido apagar o que já sabemos

  // Só o evento `incoming` traz o telefone; os retratos `call-list`/`call-status`
  // mandam apenas o endereço interno — e chegam DEPOIS. Sem esta guarda, o LID
  // sobrescrevia o número bom e o dock passava a exibir `1449…@lid`.
  const candidato = patch.phone || patch.peer;
  if (candidato) {
    // só recusa o candidato quando ele é interno E o que já temos é melhor
    const jaTemosMelhor =
      ehEnderecoInterno(candidato) &&
      anterior.peer &&
      !ehEnderecoInterno(anterior.peer);
    patch.peer = jaTemosMelhor ? anterior.peer : candidato;
  } else {
    delete patch.peer;
  }

  const resolvida = identidadeResolvida[id];
  if (resolvida) {
    if (resolvida.contact_name && !patch.contact_name && !anterior.contact_name) {
      patch.contact_name = resolvida.contact_name;
    }
    if (resolvida.peer && ehEnderecoInterno(patch.peer || anterior.peer || '')) {
      patch.peer = resolvida.peer;
    }
  }

  state.calls[id] = { ...anterior, ...patch };
};
const removeCall = id => {
  cleanupPeer(id);
  delete identidadeResolvida[id];
  delete state.calls[id];
};

const cleanupPeer = id => {
  delete state.bridged[id];
  const p = peers[id];
  if (!p) return;
  try {
    p.video?.close();
    p.localVideoStream?.getTracks().forEach(t => t.stop());
    p.localStream?.getTracks().forEach(t => t.stop());
    p.pc?.close();
    p.audioEl?.remove();
  } catch (e) {
    /* noop */
  }
  delete peers[id];
};

// ---- WebRTC: abre a ponte da chamada (áudio via track + vídeo via DataChannel) ----
const openBridge = async (sid, callId, { video = false } = {}) => {
  cleanupPeer(callId);
  const wantVideo = !!video && videoSupported();
  const localStream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video: wantVideo ? cameraConstraints() : false,
  });
  // sem STUN/TURN — o call server é ice-lite; mandamos o SDP com os candidatos já reunidos
  const pc = new RTCPeerConnection({ iceServers: [] });

  // só a track de ÁUDIO entra no PC; o vídeo vai pelo canal WebCodecs abaixo
  localStream.getAudioTracks().forEach(t => pc.addTrack(t, localStream));
  pc.addTransceiver('audio', { direction: 'recvonly' });

  const audioEl = document.createElement('audio');
  audioEl.autoplay = true;
  audioEl.dataset.astracall = callId;
  document.body.appendChild(audioEl);
  pc.ontrack = e => {
    audioEl.srcObject = e.streams[0];
  };

  // vídeo: DataChannel "h264" SEMPRE aberto (mesmo em chamada de áudio) p/ upgrade sem SDP
  const vchan = setupVideoChannel(pc);

  // câmera local começa ligada só se a chamada nasceu em vídeo
  let camTrack = wantVideo ? localStream.getVideoTracks()[0] || null : null;
  const p = {
    pc,
    audioEl,
    localStream,
    video: vchan,
    remoteVideoStream: vchan.remoteVideoStream,
    localVideoStream: null,
  };
  if (camTrack) {
    p.localVideoStream = new MediaStream([camTrack]);
    vchan.startSender(camTrack);
  }

  // liga/desliga a câmera no meio da chamada (upgrade/downgrade), sem renegociar SDP
  p.setLocalVideo = async on => {
    if (on) {
      if (camTrack) return true;
      if (!videoSupported()) return false;
      const cam = await navigator.mediaDevices.getUserMedia({
        video: cameraConstraints(),
      });
      camTrack = cam.getVideoTracks()[0] || null;
      if (!camTrack) return false;
      p.localVideoStream = new MediaStream([camTrack]);
      vchan.startSender(camTrack);
      return true;
    }
    vchan.stopSender();
    try {
      camTrack?.stop();
    } catch (e) {
      /* noop */
    }
    camTrack = null;
    p.localVideoStream = null;
    return false;
  };

  peers[callId] = p;
  state.bridged[callId] = true;

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitIceComplete(pc);
  const { sdp_answer: answer } = await rest(sid, `calls/${callId}/webrtc`, {
    body: { sdp_offer: pc.localDescription.sdp },
  });
  await pc.setRemoteDescription({ type: 'answer', sdp: answer });
};

// O FloatingCallWidget (fluxo de voz nativo) também mostra esta chamada e toca o
// ringtone em loop, porque o listener grava uma Call/mensagem de voz no ticket. Quem
// conduz a ligação AstraCall é o CallDock, então assim que ela deixa de estar
// chamando — atendida por alguém, ou encerrada — o card nativo tem que sair, senão o
// som fica tocando para sempre. Cada aba recebe os mesmos eventos SSE e limpa a sua.
const silenciarCardNativo = callId => {
  if (!callId) return;
  try {
    useCallsStore().removeCall(callId);
  } catch (e) {
    /* store ainda não montada nesta aba */
  }
};

// O call server só manda `phone` quando ELE conhece o número; fora isso vem apenas
// o endereço interno (`1449…@lid`), e era isso que aparecia no card. O nosso backend
// resolve o LID para o contato de verdade (mapeamento aprendido pelas mensagens) e
// já transmite o resultado no `kanban.astracall.incoming` — usamos ele como fonte
// autoritativa da identidade de quem está ligando.
emitter.on('kanban:astracall:incoming', data => {
  if (!data?.call_id) return;
  identidadeResolvida[data.call_id] = {
    peer: data.peer,
    contact_name: data.contact_name,
  };
  // `handoff`: a IA estava na linha e decidiu passar para um humano. Aqui o evento
  // deixa de ser só identidade e passa a CRIAR o card tocando — é o único caminho
  // de toque numa caixa com bot, e chega apenas para o atendente escolhido.
  const entregue = !!data.handoff;
  if (entregue) {
    entreguesPelaIa[data.call_id] = true;
    // O FloatingCallWidget nativo toca ringtone.mp3 EM LOOP enquanto vê a chamada
    // como não atendida. Numa transferência ela já está em andamento, então ele
    // nunca sai sozinho e o som seguia tocando depois de o atendente atender.
    silenciarCardNativo(data.call_id);
  }
  upsertCall({
    callId: data.call_id,
    peer: data.peer,
    contact_name: data.contact_name,
    conversation_id: data.conversation_id,
    ...(entregue
      ? { sid: data.sid, status: 'ringing', direction: 'incoming' }
      : {}),
  });
});

// ---- SSE ----
// Uma chamada só é ignorada enquanto NINGUÉM a entregou para este atendente e ela
// ainda está tocando: depois de aceita (ou entregue pela IA) ela precisa aparecer
// normalmente no dock, senão o atendente fica sem controle da ligação que pegou.
const deixarPassar = (sid, callId, status) => {
  // o retrato `call-list` nem sempre repete a sessão; se já conhecemos a chamada,
  // usamos a sessão que guardamos dela em vez de deixar passar por falta de dado
  const sessao = sid || state.calls[callId]?.sid;
  return (
    !temBotDeVoz(sessao) ||
    entreguesPelaIa[callId] ||
    (status && status !== 'ringing')
  );
};

const handleEvent = msg => {
  switch (msg.type) {
    case 'call-list':
      (msg.calls || [])
        .filter(c => deixarPassar(c.sid || msg.sessionId, c.callId || c.id, c.status))
        .forEach(upsertCall);
      break;
    case 'call-status':
      // faltava o filtro aqui: um `call-status` com status "ringing" criava o card
      // do mesmo jeito, e ele aparecia por um instante até o status virar outro —
      // era esse o pisca-pisca do widget nas caixas com bot de voz
      if (!deixarPassar(msg.sessionId || msg.sid, msg.id, msg.status)) break;
      upsertCall({ ...msg, callId: msg.id });
      if (msg.status && msg.status !== 'ringing') silenciarCardNativo(msg.id);
      break;
    case 'incoming':
      if (!deixarPassar(msg.sessionId, msg.id, 'ringing')) break;
      upsertCall({
        callId: msg.id,
        sid: msg.sessionId,
        status: 'ringing',
        direction: 'incoming',
        peer: msg.phone || msg.peer,
        contact_name: msg.name,
        video: msg.video,
      });
      break;
    case 'incoming-claimed': {
      silenciarCardNativo(msg.id);
      // O dono vem NO evento. Lendo do estado pegávamos o valor velho (ainda null,
      // porque o snapshot com o owner só chega depois): a própria aba que atendeu
      // apagava a chamada, e o `call-list` seguinte a recriava sem o nome nem o
      // telefone — era por isso que o card virava `…@lid` só DEPOIS de atender.
      const deOutroAtendente = msg.owner && msg.owner !== state.clientId;
      if (deOutroAtendente) removeCall(msg.id);
      else upsertCall({ callId: msg.id, owner: msg.owner });
      break;
    }
    case 'call-ended':
      logEndedCall(state.calls[msg.id], msg);
      silenciarCardNativo(msg.id);
      removeCall(msg.id);
      break;
    case 'video-state':
      // kind: 'upgrade-request' (peer pediu vídeo) | 'state' (peer ligou/desligou câmera)
      upsertCall({
        callId: msg.id,
        remoteVideo: !!msg.peerVideo,
        localVideo: !!msg.localVideo,
        upgradeIncoming:
          msg.kind === 'upgrade-request' && !!msg.upgradeIncoming,
      });
      break;
    default:
      break;
  }
};

const openStream = () => {
  if (es) es.close();
  const url = `${state.baseUrl}/api/events?clientId=${encodeURIComponent(
    state.clientId
  )}&apiKey=${encodeURIComponent(state.apiKey)}`;
  es = new EventSource(url);
  es.onmessage = ev => {
    try {
      handleEvent(JSON.parse(ev.data));
    } catch (e) {
      /* noop */
    }
  };
  es.onerror = () => {
    /* EventSource reconecta sozinho */
  };
};

// registra a ligação encerrada no histórico (Central de Ligações)
const logEndedCall = (call, ended) => {
  if (!call) return;
  const started = call.startedAt ? new Date(call.startedAt) : null;
  const dur = started ? Math.max(0, Math.round((Date.now() - started) / 1000)) : 0;
  const session = state.sessions.find(s => s.sid === call.sid);
  const answered = !!call.startedAt && call.status === 'connected';
  axios
    .post(`${state.accountBase}/kanban/call_history`, {
      conversation_id: call.conversation_id || null,
      contact_phone: call.peer || call.phone || '',
      contact_name: call.contact_name || '',
      direction: call.direction === 'incoming' ? 'INCOMING' : 'OUTGOING',
      status: answered ? 'ENDED' : 'NOT_ANSWERED',
      instance_name: session?.name || '',
      inbox_id: session?.inbox_id || null,
      duration_seconds: dur,
      call_method: 'astracall',
    })
    .catch(() => {});
};

// ---- init ----
const ensureInit = async accountId => {
  if (state.ready) return state.enabled;
  if (initPromise) return initPromise;
  state.accountBase = `/api/v1/accounts/${accountId}`;
  initPromise = (async () => {
    try {
      const { data } = await axios.get(`${state.accountBase}/kanban/astracalls/config`);
      const cfg = data.data || {};
      state.enabled = !!cfg.enabled;
      state.baseUrl = cfg.base_url || '';
      state.apiKey = cfg.api_key || '';
      state.sessions = cfg.sessions || [];
      state.clientId = getClientId();
      if (state.enabled) openStream();
    } catch (e) {
      state.enabled = false;
    } finally {
      state.ready = true;
    }
    return state.enabled;
  })();
  return initPromise;
};

// ---- ações públicas ----
const dial = async ({ sid, phone, conversationId, contactName }) => {
  const { call } = await rest(sid, 'calls', { body: { phone } });
  const callId = call.callId;
  upsertCall({
    callId,
    sid,
    status: 'starting',
    direction: 'outgoing',
    peer: phone,
    conversation_id: conversationId,
    contact_name: contactName,
  });
  await openBridge(sid, callId, {});
  return callId;
};

// Chamada que já NASCE em vídeo (`incoming` com video:true) tem que ser atendida em
// vídeo: o `openBridge` sabe subir com a câmera ligada, mas o atender sempre mandava
// `{}` e a ligação caía para só áudio — o cliente ficava vendo a própria imagem e
// nada de volta. `video` explícito permite atender só com áudio quando se quer.
const accept = async (call, { video } = {}) => {
  const comVideo = (video === undefined ? !!call.video : !!video) && videoSupported();
  const entregue = !!entreguesPelaIa[call.callId];
  try {
    // Ligação ENTREGUE PELA IA já foi aceita pela ponte. Aceitar de novo devolve
    // erro do call server e o atendente entraria numa chamada que já está de pé.
    if (!entregue) await rest(call.sid, `calls/${call.callId}/accept`);
    await openBridge(call.sid, call.callId, { video: comVideo });
    if (comVideo) upsertCall({ callId: call.callId, localVideo: true });
  } catch (e) {
    // O botão do dock chamava accept() SEM catch: qualquer falha virava rejeição
    // silenciosa e o clique parecia não fazer nada. Agora o motivo aparece.
    // eslint-disable-next-line no-console
    console.error('[AstraCall] falha ao atender', {
      callId: call.callId,
      entreguePelaIa: entregue,
      erro: e?.message || e,
    });
    throw e;
  }
};
const reject = async call => {
  await rest(call.sid, `calls/${call.callId}/reject`).catch(() => {});
  removeCall(call.callId);
};
const hangup = async call => {
  await rest(call.sid, `calls/${call.callId}`, { method: 'DELETE' }).catch(() => {});
  removeCall(call.callId);
};
const toggleMute = call => {
  const p = peers[call.callId];
  if (!p) return;
  const muted = !call.muted;
  p.localStream.getAudioTracks().forEach(t => {
    t.enabled = !muted;
  });
  upsertCall({ callId: call.callId, muted });
};

const videoAction = (call, action) =>
  rest(call.sid, `calls/${call.callId}/video/${action}`).catch(() => {});

// liga/desliga a NOSSA câmera. Ao ligar, pede upgrade ao WhatsApp; ao desligar, downgrade.
const toggleLocalVideo = async call => {
  const p = peers[call.callId];
  if (!p || !p.setLocalVideo) return false;
  const on = !call.localVideo;
  const ok = await p.setLocalVideo(on);
  await videoAction(call, ok ? 'request' : 'stop');
  upsertCall({ callId: call.callId, localVideo: ok, upgradeIncoming: false });
  return ok;
};

// responde a um pedido de vídeo recebido do peer (SSE video-state upgrade-request)
const acceptVideo = async call => {
  const p = peers[call.callId];
  if (!p || !p.setLocalVideo) return;
  await p.setLocalVideo(true);
  await videoAction(call, 'accept');
  upsertCall({ callId: call.callId, localVideo: true, upgradeIncoming: false });
};
const rejectVideo = async call => {
  await videoAction(call, 'reject');
  upsertCall({ callId: call.callId, upgradeIncoming: false });
};

// streams para o CallDock ligar nos <video> (fora do reactive — MediaStream não vira proxy)
const remoteVideoStreamFor = callId => peers[callId]?.remoteVideoStream || null;
const localVideoStreamFor = callId => peers[callId]?.localVideoStream || null;

// ---- getters ----
const callList = computed(() => Object.values(state.calls));
// Só é "ativa" a chamada cuja ponte de áudio foi aberta NESTA aba (dial/accept).
// Assim, chamadas presas no call server que chegam via call-list (fantasmas, sem
// call-ended) não reaparecem travadas no dock a cada reload nem vazam de outro atendente.
const activeCalls = computed(() =>
  callList.value.filter(
    c =>
      (c.direction !== 'incoming' || c.status !== 'ringing') && !!state.bridged[c.callId]
  )
);
const incomingCalls = computed(() =>
  callList.value.filter(c => c.direction === 'incoming' && c.status === 'ringing')
);

export function useAstraCalls() {
  return {
    state,
    ensureInit,
    dial,
    accept,
    reject,
    hangup,
    toggleMute,
    toggleLocalVideo,
    acceptVideo,
    rejectVideo,
    remoteVideoStreamFor,
    localVideoStreamFor,
    videoSupported: videoSupported(),
    callList,
    activeCalls,
    incomingCalls,
    sessionForInbox: inboxId =>
      state.sessions.find(s => String(s.inbox_id) === String(inboxId)),
  };
}
